import { TableCell, TableRow } from "@/components/ui/table";
import { CostComponent } from "@/types/project";
import { formatCurrency } from "@/utils/format";
import { calculateAnticipatedFinal, calculateVariance } from "@/utils/cost-calculations";
import { CostInput } from "./components/CostInput";
import { CommentDialog } from "./components/CommentDialog";
import { VarianceDisplay } from "./components/VarianceDisplay";

interface CostTableRowProps {
  label: string;
  component: CostComponent & { comment?: string };
  onChange: (field: keyof CostComponent, value: number | null) => void;
  onCommentChange: (comment: string) => void;
}

export function CostTableRow({
  label,
  component,
  onChange,
  onCommentChange,
}: CostTableRowProps) {
  const anticipatedFinal = calculateAnticipatedFinal(component);
  const variance = calculateVariance(component);

  return (
    <TableRow className="hover:bg-muted/50" aria-label={`${label} Row`}>
      <TableCell aria-label={`${label} Label`}>{label}</TableCell>
      <TableCell>
        <CostInput
          value={component.budgeted}
          onChange={(value) => onChange("budgeted", value)}
          aria-label={`${label} Budgeted Input`}
        />
      </TableCell>
      <TableCell>
        <CostInput
          value={component.toDate}
          onChange={(value) => onChange("toDate", value)}
          aria-label={`${label} To Date Input`}
        />
      </TableCell>
      <TableCell>
        <CostInput
          value={component.forecast}
          onChange={(value) => onChange("forecast", value)}
          aria-label={`${label} Forecast Input`}
        />
      </TableCell>
      <TableCell aria-label={`${label} Anticipated Final`}>
        {formatCurrency(anticipatedFinal)}
      </TableCell>
      <TableCell aria-label={`${label} Variance`}>
        <VarianceDisplay variance={variance} />
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <CommentDialog
            label={label}
            comment={component.comment || ""}
            onCommentChange={onCommentChange}
            aria-label={`${label} Comment Dialog`}
          />
          {component.comment && (
            <span
              className="text-sm text-muted-foreground truncate max-w-[200px]"
              aria-label={`${label} Comment`}
            >
              {component.comment}
            </span>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}
